import { Component, PLATFORM_ID, Inject, OnInit, OnDestroy } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { map, delay, withLatestFrom, filter } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import * as AOS from 'aos';
import { DOCUMENT } from '@angular/common';
import { AuthService } from './Auth/service/auth.service';
import { TranslateService } from '@ngx-translate/core';
import { ActivityService } from './Data/service/Activity.Service';
import { NotificationService, NotifBannerState } from './Data/service/notification.service';
import { LanguageService } from './shared/services/language.service';
import { FirebaseFcmService } from './firebase/firebase-fcm.service';
import { NotificationSyncService } from './shared/services/notification-sync.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {

  loaders = this.loader.progress$.pipe(
    delay(1000),
    withLatestFrom(this.loader.progress$),
    map(v => v[1]),
  );

  title = 'Fawtara';
  isLogin = false;
  lang: string = 'en';
  curentLang: any;

  notifBannerState: NotifBannerState = null;
  notifBannerVisible = false;
  private notifBannerTimeout: ReturnType<typeof setTimeout> | null = null;
  private notifFocusHandler = () => this.notificationService.refreshBannerState();

  /**
   * Landing pages that embed `app-thi-landing-header` should not also show
   * the legacy `app-navigathion` bar (double header).
   */
  showLegacyPublicNav = true;
  private routerSub?: Subscription;

  private static readonly THI_SELF_CONTAINED_PATHS = new Set([
    '',
    '/',
    '/home',
    '/landing',
    '/articles',
    '/pricing',
    '/support',
    '/about',
    '/login',
    '/register',
    '/forgot-password',
    '/privacy-policy',
    '/terms-of-service',
    '/terms',
  ]);

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private document: Document,
    private authService: AuthService,
    private router: Router,
    public notificationService: NotificationService,
    public translate: TranslateService,
    private loader: LoadingBarService,
    private activityService: ActivityService,
    private languageService: LanguageService,
    private firebaseFcm: FirebaseFcmService,
    private notificationSync: NotificationSyncService,
  ) {
    this.authService.isLogin.subscribe(loggedIn => {
      this.isLogin = loggedIn;
      if (loggedIn) {
        const uid = localStorage.getItem('user_id');
        if (uid) {
          this.notificationService.checkPermissionAndRequest(Number(uid));
        }
        this.notificationSync.init();
      } else {
        this.notificationSync.reset();
      }
    });
  }

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.firebaseFcm.ensureForegroundListener();
    }
    this.refreshLegacyNavVisibility();
    this.routerSub = this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.refreshLegacyNavVisibility();
      }
    });

    this.activityService.checkInactivity();
    if (isPlatformBrowser(this.platformId) && this.document.querySelector('[data-aos]')) {
      AOS.init({ once: true, duration: 600 });
    }
    this.languageService.bootstrap();
    this.curentLang = this.languageService.getCurrentLang();

    if (isPlatformBrowser(this.platformId)) {
      window.addEventListener('focus', this.notifFocusHandler);
    }

    this.notificationService.bannerState$.subscribe(state => {
      if (state) {
        this.notifBannerState = state;
        if (this.notifBannerTimeout) {
          clearTimeout(this.notifBannerTimeout);
        }
        setTimeout(() => { this.notifBannerVisible = true; }, 20);
      } else {
        this.notifBannerVisible = false;
        this.notifBannerTimeout = setTimeout(() => { this.notifBannerState = null; }, 400);
      }
    });
  }

  allowNotifications() {
    this.notificationService.grantPermission();
  }

  dismissNotifBanner() {
    this.notificationService.dismissBanner();
  }

  private refreshLegacyNavVisibility(): void {
    const p = (this.router.url || '').split('?')[0] || '/';
    if (p.startsWith('/articles/')) {
      this.showLegacyPublicNav = false;
      return;
    }
    this.showLegacyPublicNav = !AppComponent.THI_SELF_CONTAINED_PATHS.has(p);
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
    if (isPlatformBrowser(this.platformId)) {
      window.removeEventListener('focus', this.notifFocusHandler);
    }
  }
}


